import type { ViewMode } from '@better-lights/core';
import { cachedDepth } from '../depth/client.ts';
import { sceneFor } from '../editor/scene.ts';
import { decodeRaw } from '../photo/import.ts';
import { createRenderer } from '../renderer/create.ts';
import type { Pixels } from '../renderer/types.ts';
import { shaOfUri } from '../storage/blobStore.ts';
import { storage } from '../storage/index.ts';
import type { ProjectFile } from '../storage/record.ts';

/**
 * Renders a saved project at full photo resolution without the editor (quote PDFs from the
 * project screen): a temporary WebGL2 renderer on a detached canvas, disposed afterwards.
 */
export async function renderProjectPixels(
  file: ProjectFile,
  views: ViewMode[],
  customHex: string,
): Promise<Record<string, Pixels>> {
  const blob = await storage().blobs.getByUri(file.photo.uri);
  if (!blob) throw new Error('The photo is missing.');
  const bitmap = await decodeRaw(blob);
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;
  const renderer = createRenderer(canvas);
  try {
    renderer.setPhoto(bitmap);
    const sha = shaOfUri(file.photo.uri);
    renderer.setDepth(sha ? await cachedDepth(sha).catch(() => null) : null);
    const scene = sceneFor(
      {
        photo: file.photo,
        runs: file.doc.runs,
        time: file.doc.time,
        style: file.doc.style,
        colorId: file.doc.colorId,
        frameWidthFt: file.doc.frameWidthFt,
      },
      customHex,
    );
    if (!scene) throw new Error('The photo is missing.');
    const out: Record<string, Pixels> = {};
    for (const v of views) {
      renderer.render(scene, v);
      out[v] = renderer.readScene();
    }
    return out;
  } finally {
    renderer.dispose();
    bitmap.close();
  }
}
